import React from 'react';
import { useNavigate } from 'react-router-dom';
import { BookOpen, Bell, Scale, Database, Users, GraduationCap, ArrowRight } from 'lucide-react';

interface HelpCardProps {
  title: string;
  subtitle: string;
  actionLabel: string;
  onAction: () => void;
  icon: React.ReactNode;
}

const HelpCard: React.FC<HelpCardProps> = ({ title, subtitle, actionLabel, onAction, icon }) => (
  <div className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm">
    <div className="flex items-start justify-between gap-3 mb-3">
      <div className="p-2 rounded-lg bg-indigo-50 text-indigo-600">{icon}</div>
      <button
        onClick={onAction}
        className="inline-flex items-center gap-1 text-sm font-medium text-indigo-600 hover:text-indigo-700"
      >
        {actionLabel} <ArrowRight size={14} />
      </button>
    </div>
    <h3 className="text-base font-semibold text-slate-800">{title}</h3>
    <p className="text-sm text-slate-500 mt-1">{subtitle}</p>
  </div>
);

export const HelpView: React.FC = () => {
  const navigate = useNavigate();

  return (
    <div className="space-y-6 animate-fade-in">
      <section className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
        <h2 className="text-xl font-bold text-slate-800">What should I do now?</h2>
        <p className="text-slate-500 mt-2">
          Start with pending actions, then complete governance and training queues, and finally maintain data quality in inventory and team directory.
        </p>
      </section>

      <section>
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3">Priority actions</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <HelpCard
            title="Process pending requests"
            subtitle="Review and resolve access/training requests first."
            actionLabel="Go to Notifications"
            onAction={() => navigate('/admin/notifications')}
            icon={<Bell size={18} />}
          />
          <HelpCard
            title="Close governance risks"
            subtitle="Handle overdue and next-30-days governance tasks."
            actionLabel="Go to Governance"
            onAction={() => navigate('/admin/governance')}
            icon={<Scale size={18} />}
          />
          <HelpCard
            title="Reduce training debt"
            subtitle="Track incomplete curricula and send reminders."
            actionLabel="Go to Training"
            onAction={() => navigate('/admin/training')}
            icon={<GraduationCap size={18} />}
          />
        </div>
      </section>

      <section>
        <h3 className="text-sm font-bold text-slate-500 uppercase tracking-wide mb-3">Operational guides</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <HelpCard
            title="Inventory operations"
            subtitle="Create and maintain tools with valid ownership and lifecycle dates."
            actionLabel="Open Inventory"
            onAction={() => navigate('/admin/inventory')}
            icon={<Database size={18} />}
          />
          <HelpCard
            title="Team directory operations"
            subtitle="Keep employee roles, teams, and assignments accurate."
            actionLabel="Open Team Directory"
            onAction={() => navigate('/admin/employees')}
            icon={<Users size={18} />}
          />
          <HelpCard
            title="Documentation pack"
            subtitle="Quickstarts and task guides are available in the docs folder."
            actionLabel="Open Settings"
            onAction={() => navigate('/admin/settings')}
            icon={<BookOpen size={18} />}
          />
        </div>
      </section>
    </div>
  );
};
